import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage, type Messaging, type MessagePayload } from 'firebase/messaging';
import type { AxiosRequestConfig } from 'axios';
import api from '@/services/api';
import {
  firebaseNotificationConfig,
  hasFirebaseNotificationConfig,
} from '@/support/firebaseNotificationsConfig';

type ApiNotification = {
  id: number | string;
  shop_id?: number | string | null;
  shop?: {
    id?: number | string | null;
    name?: string | null;
    code?: string | null;
  } | null;
  title: string;
  body?: string | null;
  type?: string | null;
  data?: Record<string, unknown> | null;
};

type ToastAwareRequestConfig = AxiosRequestConfig & {
  skipErrorToast?: boolean;
  skipSuccessToast?: boolean;
};

export type WebNotificationDiagnostics = {
  available: boolean;
  configured: boolean;
  missingConfigKeys: string[];
  permission: NotificationPermission | 'unsupported';
  secureContext: boolean;
  serviceWorkerSupported: boolean;
  firebaseServiceWorkerRegistered: boolean;
  firebaseServiceWorkerScope: string;
  fcmTokenAvailable: boolean;
  deviceUuid: string | null;
  origin: string;
  lastError: string | null;
};

const DEVICE_UUID_KEY = 'suite241_notification_device_uuid';
const DELIVERED_IDS_KEY = 'suite241_web_notification_delivered_ids';
const POLLING_INTERVAL_MS = 45000;
const FIREBASE_SERVICE_WORKER_SCOPE = '/firebase-cloud-messaging-push-scope';

let firebaseApp: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let pollingTimer: number | null = null;
let foregroundUnsubscribe: (() => void) | null = null;
let started = false;
let initialized = false;

const hasFirebaseConfig = (): boolean => {
  return hasFirebaseNotificationConfig();
};

const browserName = (): string => {
  const ua = navigator.userAgent || '';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'Safari';
  if (/Firefox\//.test(ua)) return 'Firefox';
  return 'Navigateur web';
};

export const getNotificationDeviceUuid = (): string => {
  const existing = localStorage.getItem(DEVICE_UUID_KEY);
  if (existing) return existing;

  const randomUuid = typeof crypto !== 'undefined'
    ? (crypto as Crypto & { randomUUID?: () => string }).randomUUID
    : null;
  const value = typeof randomUuid === 'function'
    ? randomUuid.call(crypto)
    : `device-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  localStorage.setItem(DEVICE_UUID_KEY, value);
  return value;
};

const deliveredIds = (): Set<string> => {
  try {
    const raw = localStorage.getItem(DELIVERED_IDS_KEY);
    return new Set(Array.isArray(JSON.parse(raw || '[]')) ? JSON.parse(raw || '[]').map(String) : []);
  } catch (error) {
    return new Set();
  }
};

const rememberDeliveredIds = (ids: Set<string>): void => {
  localStorage.setItem(DELIVERED_IDS_KEY, JSON.stringify(Array.from(ids).slice(-250)));
};

const markLocalDelivered = (id: number | string): boolean => {
  const ids = deliveredIds();
  const key = String(id);
  if (ids.has(key)) return false;
  ids.add(key);
  rememberDeliveredIds(ids);
  return true;
};

const confirmDelivered = async (ids: Array<number | string>): Promise<void> => {
  if (!ids.length) return;

  await api.post('/notifications/web-deliveries', {
    device_uuid: getNotificationDeviceUuid(),
    notification_ids: ids,
  }, { skipErrorToast: true } as ToastAwareRequestConfig);
};

const notificationUrl = (notification: ApiNotification): string => {
  const dataUrl = notification.data?.url;
  return typeof dataUrl === 'string' && dataUrl ? dataUrl : '/backoffice/tableau-de-bord';
};

const notificationShopName = (notification: ApiNotification): string => {
  const relationName = notification.shop?.name;
  if (typeof relationName === 'string' && relationName.trim()) return relationName.trim();

  const dataName = notification.data?.shop_name;
  return typeof dataName === 'string' ? dataName.trim() : '';
};

const notificationTitle = (notification: ApiNotification): string => {
  const baseTitle = notification.title || 'Suite241 Gestion Commerciale';
  const shopName = notificationShopName(notification);
  const prefix = `${shopName} · `;

  return shopName && !baseTitle.startsWith(prefix) ? `${prefix}${baseTitle}` : baseTitle;
};

const showNativeNotification = async (notification: ApiNotification): Promise<boolean> => {
  if (Notification.permission !== 'granted') return false;
  if (!markLocalDelivered(notification.id)) return false;

  const registration = await navigator.serviceWorker.ready;
  await registration.showNotification(notificationTitle(notification), {
    body: notification.body || '',
    tag: `suite241-notification-${notification.shop_id || notification.data?.shop_id || 'global'}-${notification.id}`,
    renotify: false,
    icon: '/img/icons/android-chrome-192x192.png',
    badge: '/img/icons/android-chrome-192x192.png',
    data: {
      notification_id: String(notification.id),
      shop_id: String(notification.shop_id || notification.data?.shop_id || ''),
      shop_name: notificationShopName(notification),
      url: notificationUrl(notification),
    },
  });

  await confirmDelivered([notification.id]);
  window.dispatchEvent(new CustomEvent('suite241:notifications-changed'));

  return true;
};

const notificationFromPayload = (payload: MessagePayload): ApiNotification | null => {
  const id = payload.data?.notification_id;
  if (!id) return null;
  const shopId = payload.data?.shop_id || null;
  const shopName = payload.data?.shop_name || null;

  return {
    id,
    shop_id: shopId,
    shop: shopName ? { id: shopId, name: shopName } : null,
    title: payload.notification?.title || payload.data?.title || 'Suite241 Gestion Commerciale',
    body: payload.notification?.body || payload.data?.body || '',
    type: payload.data?.type || 'info',
    data: {
      url: payload.data?.url || '/backoffice/tableau-de-bord',
    },
  };
};

const serviceWorkerUrl = (): string => {
  const params = new URLSearchParams(Object.entries(firebaseNotificationConfig().appConfig));
  return `/firebase-messaging-sw.js?${params.toString()}`;
};

const disableFirebaseMessaging = async (): Promise<void> => {
  if (foregroundUnsubscribe) {
    foregroundUnsubscribe();
    foregroundUnsubscribe = null;
  }

  firebaseApp = null;
  messaging = null;
  initialized = false;
  started = false;

  if (
    typeof navigator === 'undefined'
    || !navigator.serviceWorker
    || typeof navigator.serviceWorker.getRegistration !== 'function'
  ) {
    return;
  }

  const registration = await navigator.serviceWorker
    .getRegistration(FIREBASE_SERVICE_WORKER_SCOPE)
    .catch(() => null);

  await registration?.unregister().catch(() => undefined);
};

const firebaseMessaging = async (): Promise<Messaging | null> => {
  if (!hasFirebaseConfig()) return null;
  if (!(await isSupported())) return null;

  if (!firebaseApp) {
    firebaseApp = initializeApp(firebaseNotificationConfig().appConfig);
  }

  if (!messaging) {
    messaging = getMessaging(firebaseApp);
  }

  return messaging;
};

const fcmToken = async (): Promise<string | null> => {
  const currentMessaging = await firebaseMessaging();
  const vapidKey = firebaseNotificationConfig().vapidKey;

  if (!currentMessaging || !vapidKey) return null;

  const registration = await navigator.serviceWorker.register(serviceWorkerUrl(), {
    scope: FIREBASE_SERVICE_WORKER_SCOPE,
  });

  return getToken(currentMessaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });
};

export const webNotifications = {
  isAvailable(): boolean {
    return typeof window !== 'undefined'
      && 'Notification' in window
      && typeof navigator !== 'undefined'
      && Boolean(navigator.serviceWorker);
  },

  isConfigured(): boolean {
    return hasFirebaseConfig();
  },

  permission(): NotificationPermission | 'unsupported' {
    if (!this.isAvailable() || !this.isConfigured()) return 'unsupported';
    return Notification.permission;
  },

  async diagnostics(): Promise<WebNotificationDiagnostics> {
    const config = firebaseNotificationConfig();
    const serviceWorkerSupported = typeof navigator !== 'undefined'
      && Boolean(navigator.serviceWorker)
      && typeof navigator.serviceWorker.getRegistration === 'function';
    const available = this.isAvailable();
    const configured = this.isConfigured();
    const permission = this.permission();
    let firebaseServiceWorkerRegistered = false;
    let fcmTokenAvailable = false;
    let lastError: string | null = null;

    if (serviceWorkerSupported) {
      const registration = await navigator.serviceWorker
        .getRegistration(FIREBASE_SERVICE_WORKER_SCOPE)
        .catch((error) => {
          lastError = error instanceof Error ? error.message : String(error);
          return null;
        });
      firebaseServiceWorkerRegistered = Boolean(registration);
    }

    if (available && configured && permission === 'granted') {
      const token = await fcmToken().catch((error) => {
        lastError = error instanceof Error ? error.message : String(error);
        return null;
      });
      fcmTokenAvailable = Boolean(token);

      if (serviceWorkerSupported) {
        const registration = await navigator.serviceWorker
          .getRegistration(FIREBASE_SERVICE_WORKER_SCOPE)
          .catch(() => null);
        firebaseServiceWorkerRegistered = Boolean(registration);
      }
    }

    return {
      available,
      configured,
      missingConfigKeys: config.missingKeys,
      permission,
      secureContext: typeof window !== 'undefined' ? window.isSecureContext === true : false,
      serviceWorkerSupported,
      firebaseServiceWorkerRegistered,
      firebaseServiceWorkerScope: FIREBASE_SERVICE_WORKER_SCOPE,
      fcmTokenAvailable,
      deviceUuid: typeof window !== 'undefined' ? getNotificationDeviceUuid() : null,
      origin: typeof window !== 'undefined' ? window.location.origin : '',
      lastError,
    };
  },

  async enable(): Promise<boolean> {
    if (!this.isAvailable() || !this.isConfigured()) {
      await disableFirebaseMessaging();
      return false;
    }

    const permission = await Notification.requestPermission();
    const token = permission === 'granted' ? await fcmToken().catch(() => null) : null;

    await api.post('/notification-devices', {
      device_uuid: getNotificationDeviceUuid(),
      fcm_token: token,
      permission,
      browser: browserName(),
    }, { skipSuccessToast: true } as ToastAwareRequestConfig);

    if (permission !== 'granted') return false;

    await this.start();
    return true;
  },

  async start(): Promise<void> {
    if (!this.isAvailable() || !this.isConfigured()) {
      await disableFirebaseMessaging();
      return;
    }

    if (started || Notification.permission !== 'granted') return;

    started = true;
    const token = await fcmToken().catch(() => null);

    await api.post('/notification-devices', {
      device_uuid: getNotificationDeviceUuid(),
      fcm_token: token,
      permission: Notification.permission,
      browser: browserName(),
    }, {
      skipErrorToast: true,
      skipSuccessToast: true,
    } as ToastAwareRequestConfig);

    if (!initialized) {
      const currentMessaging = await firebaseMessaging().catch(() => null);
      if (currentMessaging) {
        foregroundUnsubscribe = onMessage(currentMessaging, async (payload) => {
          const notification = notificationFromPayload(payload);
          if (notification) {
            await showNativeNotification(notification).catch(() => undefined);
          }
        });
      }

      initialized = true;
    }

    await this.pollPending();

    if (!pollingTimer) {
      pollingTimer = window.setInterval(() => {
        this.pollPending().catch(() => undefined);
      }, POLLING_INTERVAL_MS);
    }
  },

  stop(): void {
    started = false;

    if (pollingTimer) {
      window.clearInterval(pollingTimer);
      pollingTimer = null;
    }

    if (foregroundUnsubscribe) {
      foregroundUnsubscribe();
      foregroundUnsubscribe = null;
    }
  },

  async pollPending(): Promise<void> {
    if (!this.isAvailable() || !this.isConfigured() || Notification.permission !== 'granted') return;

    const response = await api.get('/notifications/web-pending', {
      params: { device_uuid: getNotificationDeviceUuid() },
      skipErrorToast: true,
    } as ToastAwareRequestConfig);
    const notifications = Array.isArray(response.data?.data) ? response.data.data as ApiNotification[] : [];

    const displayed: Array<number | string> = [];
    for (const notification of notifications) {
      const didDisplay = await showNativeNotification(notification).catch(() => false);
      if (didDisplay) {
        displayed.push(notification.id);
      }
    }

    if (displayed.length) {
      window.dispatchEvent(new CustomEvent('suite241:notifications-changed'));
    }
  },
};

export default webNotifications;
